import { useCallback } from 'react';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import { v4 as uuid } from 'uuid';
import { graphql, commitMutation } from 'react-relay';
import { usePublish } from '@atlaskit/rovo-triggers';
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	fireOperationalAnalytics,
	useAnalyticsEvents,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	utils_triggerRovoAgentMutation,
	utils_triggerRovoAgentMutation$data,
} from '@atlassian/jira-relay/src/__generated__/utils_triggerRovoAgentMutation.graphql';
import type {
	utils_triggerRovoAgentInBulkMutation,
	utils_triggerRovoAgentInBulkMutation$data,
} from '@atlassian/jira-relay/src/__generated__/utils_triggerRovoAgentInBulkMutation.graphql';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';

import { useFlagService, type DismissFlag, type ShowFlagFn } from '@atlassian/jira-flags';
import { useIntl, type IntlShape } from '@atlassian/jira-intl';

import { DISMISS_COMMAND } from '@atlassian/jira-flags/src/services/types.tsx';
import { isAgentTriggeredJobInProgress } from '@atlassian/jira-autodev-common/src/common/utils.tsx';
import {
	PROCESSING_SCREENS,
	ROVO_CHAT_QUERYPARAM_KEY,
} from '@atlassian/jira-autodev-common/src/common/constants.tsx';
import type { Screen } from '@atlassian/jira-autodev-common/src/common/types.tsx';
import type {
	JiraAutodevStatus,
	JiraAutodevPhase,
} from '@atlassian/jira-relay/src/__generated__/uiGetAutodevJobsQuery.graphql';

import messages from './messages.tsx';
import { BULK_LIMIT } from './constants.tsx';

type ErrorDetails = {
	statusCode?: number | null;
	errorType?: string | null;
};

const onTriggerAgentError = ({
	agentId,
	issueAri,
	showFlag,
	dismissFlag,
	formatMessage,
	error,
	errorDetails,
	duration,
	openChat,
}: {
	agentId: string;
	issueAri: string;
	showFlag: ShowFlagFn;
	dismissFlag: (arg1: DismissFlag) => void;
	formatMessage: IntlShape['formatMessage'];
	error?: Error;
	errorDetails?: ErrorDetails;
	duration: number;
	openChat?: () => void;
}) => {
	const flagId = uuid();
	fireErrorAnalytics({
		error: error ?? new Error('Failed to trigger Autodev Rovo agent'),
		meta: {
			id: 'triggerAutodevRovoAgent',
			packageName: 'jiraAutodev',
			teamName: 'autodev',
		},
		attributes: {
			agentId,
			issueAri,
			duration,
			...(errorDetails ?? {}),
		},
		sendToPrivacyUnsafeSplunk: true,
	});
	showFlag({
		messageId: 'autodev-agent-picker.ui.picker-content.show-flag.error',
		messageType: 'transactional',
		id: flagId,
		type: 'error',
		title: formatMessage(messages.agentPickerErrorFlagTitle),
		description: formatMessage(messages.agentPickerErrorFlagDescription),
		actions: openChat
			? [
					{
						content: formatMessage(messages.agentPickerErrorFlagAction),
						onClick: () => {
							openChat();
							dismissFlag({ id: flagId, command: DISMISS_COMMAND });
						},
					},
				]
			: [],
	});
};

type onTriggerAgentMutationErrorFn = (agentId: string, duration: number, e?: Error) => void;

export const useOnTriggerAgentMutationError = (issueAri: string): onTriggerAgentMutationErrorFn => {
	const { showFlag, dismissFlag } = useFlagService();
	const { formatMessage } = useIntl();

	return (agentId: string, duration: number, e?: Error) =>
		onTriggerAgentError({
			agentId,
			issueAri,
			showFlag,
			dismissFlag,
			formatMessage,
			error: e,
			duration,
		});
};

const extractErrorFromResponse = (data: utils_triggerRovoAgentMutation$data) => {
	const { extensions, message } = data.devai_invokeAutodevRovoAgent?.errors?.[0] ?? {
		message: 'Failed to trigger Autodev Rovo agent',
		extensions: null,
	};

	const { errorType, statusCode } = extensions ?? {
		errorType: 'unknown',
		statusCode: 500,
	};

	const errorDetails = { statusCode, errorType };

	return { errorDetails, message };
};

type onTrigggerAgentMutationCompletedFn = (
	data: utils_triggerRovoAgentMutation$data,
	agentId: string,
	duration: number,
) => void;

export const useOnTrigggerAgentMutationCompleted = (
	issueAri: string,
	onMutationSuccess: () => void,
	setIsTriggeringAutodevInprogress?: (isInProgres: boolean) => void,
): onTrigggerAgentMutationCompletedFn => {
	const { showFlag, dismissFlag } = useFlagService();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { openChat } = useRovoChat();

	return (data: utils_triggerRovoAgentMutation$data, agentId: string, duration: number) => {
		if (data.devai_invokeAutodevRovoAgent?.success !== true) {
			const { message, errorDetails } = extractErrorFromResponse(data);
			const conversationId = data.devai_invokeAutodevRovoAgent?.conversationId;
			const shouldReviewInRovoChat = ['action_not_returned', 'action_execution_failed'].includes(
				errorDetails.errorType ?? '',
			);
			setIsTriggeringAutodevInprogress?.(false);
			onTriggerAgentError({
				agentId,
				issueAri,
				showFlag,
				dismissFlag,
				formatMessage,
				duration,
				error: message ? new Error(message) : undefined,
				errorDetails,
				openChat:
					shouldReviewInRovoChat && conversationId != null
						? () => openChat(conversationId)
						: undefined,
			});

			return;
		}

		fireOperationalAnalytics(createAnalyticsEvent({}), 'triggerAutodevRovoAgent success', {
			agentId,
			issueAri,
			duration,
		});

		onMutationSuccess();
	};
};

interface FailedIssueInfo {
	issueKey: string;
	issueUrl: string;
	conversationId?: string;
}

const onTriggerAgentInBulkError = ({
	agentId,
	agentName,
	failedIssueInfo,
	issueAris,
	projectKey,
	showFlag,
	formatMessage,
	error,
	errorDetails,
	duration,
	isSimplifiedProject,
}: {
	agentId: string;
	agentName: string;
	issueAris: string[];
	projectKey: string;
	failedIssueInfo: FailedIssueInfo[];
	showFlag: ShowFlagFn;
	formatMessage: IntlShape['formatMessage'];
	error: Error;
	errorDetails?: ErrorDetails;
	duration: number;
	isSimplifiedProject: boolean;
}) => {
	fireErrorAnalytics({
		error,
		meta: {
			id: 'triggerAutodevRovoAgentInBulk',
			packageName: 'jiraAutodev',
			teamName: 'autodev',
		},
		attributes: {
			agentId,
			issueAris: issueAris.toString(),
			duration,
			numTotalIssues: issueAris.length,
			numFailedIssues: failedIssueInfo.length,
			numFailedIssuesWithoutConversationId: failedIssueInfo.filter(
				(failedIssue) => failedIssue.conversationId == null,
			).length,
			...(errorDetails ?? {}),
		},
		sendToPrivacyUnsafeSplunk: true,
	});
	const issuesWithErrorCount = failedIssueInfo.length;
	if (issuesWithErrorCount === 0) {
		// This is success: false case that should rarely happen in real world, when something went seriously wrong.
		showFlag({
			messageId:
				'autodev-agent-picker.ui.picker-content.show-flag.error.picker-content-bulk-run-unexpected.show-flag.error',
			messageType: 'transactional',
			type: 'error',
			title: formatMessage(messages.agentPickerInBulkErrorUnexpectedFlagTitle),
			testId: 'autodev-agent-picker.ui.picker-content.invoke-autodev-failure-flag',
		});
		return;
	}
	const successfulIssueCount = issueAris.length - failedIssueInfo.length;
	if (successfulIssueCount > 0) {
		showFlag({
			messageId:
				'autodev-agent-picker.ui.picker-content.show-flag.success.error.picker-content-bulk-run.show-flag.error',
			messageType: 'transactional',
			type: 'success',
			title: formatMessage(messages.agentPickerInBulkAllSuccessFlagTitle, {
				agentName,
				issueCount: successfulIssueCount,
			}),
			description: fg('jira-development-work-items-agent-tab')
				? undefined
				: formatMessage(messages.agentPickerInBulkAllSuccessFlagDescriptionBeforeDevHub),
			actions: fg('jira-development-work-items-agent-tab')
				? [
						{
							content: formatMessage(messages.viewInDevelopment),
							href: `/jira/software/${isSimplifiedProject ? '' : 'c/'}projects/${projectKey}/development?tab=AGENTS`,
							target: '_blank',
							onClick: (_?: React.MouseEvent<HTMLElement>, analyticsEvent?: UIAnalyticsEvent) => {
								if (analyticsEvent != null) {
									fireUIAnalytics(analyticsEvent, 'viewInDevelopment', {
										flagType: 'afterSomeFailed',
									});
								}
							},
						},
					]
				: undefined,
		});
	}
	showFlag({
		messageId:
			'autodev-agent-picker.ui.picker-content.show-flag.error.picker-content-bulk-run.show-flag.error',
		messageType: 'transactional',
		type: 'error',
		title: formatMessage(messages.agentPickerInBulkErrorFlagTitle, {
			agentName,
			errorIssueCount: issuesWithErrorCount,
		}),
		description: formatMessage(messages.agentPickerInBulkErrorFlagDescription),
		actions: failedIssueInfo
			// We can't do much when there isn't conversaion id returned, but this should not happen in real world.
			.filter((failedIssue) => failedIssue.conversationId != null)
			.map((failedIssue) => ({
				// TODO: This is current not issue key, but issue ARI. This should be updated to populate issue key correctly, when issue hydration is in place
				content: failedIssue.issueKey,
				href: `${failedIssue.issueUrl}?${ROVO_CHAT_QUERYPARAM_KEY}=${failedIssue.conversationId}`,
				target: '_blank',
				onClick: (_?: React.MouseEvent<HTMLElement>, analyticsEvent?: UIAnalyticsEvent) => {
					if (analyticsEvent != null) {
						fireUIAnalytics(analyticsEvent, 'agentBulkErrorLink');
					}
				},
			})),
		testId: 'autodev-agent-picker.ui.picker-content.invoke-autodev-partial-failure-flag',
	});
};

interface onTriggerAgentInBulkMutationErrorFnArgs {
	agentId: string;
	agentName: string;
	duration: number;
	e: Error;
	failedIssueInfo: FailedIssueInfo[];
}
type onTriggerAgentInBulkMutationErrorFn = (args: onTriggerAgentInBulkMutationErrorFnArgs) => void;

export const useOnTriggerAgentInBulkMutationError = (
	issueAris: string[],
	projectKey: string,
): onTriggerAgentInBulkMutationErrorFn => {
	const { showFlag } = useFlagService();
	const { formatMessage } = useIntl();
	const isSimplifiedProject = getIsSimplifiedProject();

	return ({
		agentId,
		agentName,
		duration,
		e,
		failedIssueInfo,
	}: onTriggerAgentInBulkMutationErrorFnArgs) =>
		onTriggerAgentInBulkError({
			agentId,
			agentName,
			issueAris,
			projectKey,
			failedIssueInfo,
			showFlag,
			formatMessage,
			error: e,
			duration,
			isSimplifiedProject,
		});
};

const extractErrorFromBulkMutationResponse = (data: utils_triggerRovoAgentInBulkMutation$data) => {
	const { extensions, message } = data.devai_invokeAutodevRovoAgentInBulk?.errors?.[0] ?? {
		message: 'Failed to trigger Autodev Rovo agent in bulk',
		extensions: null,
	};

	const { errorType, statusCode } = extensions ?? {
		errorType: 'unknown',
		statusCode: 500,
	};

	const errorDetails = { statusCode, errorType };

	return { errorDetails, message };
};

interface onTrigggerAgentInBulkMutationCompletedFnArgs {
	data: utils_triggerRovoAgentInBulkMutation$data;
	agentId: string;
	agentName: string;
	issueCount: number;
	duration: number;
}
type onTrigggerAgentInBulkMutationCompletedFn = (
	args: onTrigggerAgentInBulkMutationCompletedFnArgs,
) => void;

export const useOnTrigggerAgentInBulkMutationCallbacks = (
	issueAris: string[],
	projectKey: string,
): {
	onTriggerMutationInBulkCompleted: onTrigggerAgentInBulkMutationCompletedFn;
	onTriggerMutationInBulkStarted: (agentName: string, issueCount: number) => void;
	onTriggerMutationInBulkOverLimit: (issueCount: number) => void;
} => {
	const { showFlag } = useFlagService();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSimplifiedProject = getIsSimplifiedProject();

	return {
		onTriggerMutationInBulkCompleted: ({
			data,
			agentId,
			agentName,
			issueCount,
			duration,
		}: onTrigggerAgentInBulkMutationCompletedFnArgs) => {
			const failedIssues = data.devai_invokeAutodevRovoAgentInBulk?.failedIssues ?? [];
			const successfulIssues = data.devai_invokeAutodevRovoAgentInBulk?.succeededIssues ?? [];
			fireTrackAnalytics(createAnalyticsEvent({}), 'jiraFlag viewed', 'agentBulkFlagResult', {
				numSuccessfulIssues: successfulIssues.length,
				numFailedIssues: failedIssues.length,
			});
			if (data.devai_invokeAutodevRovoAgentInBulk?.success !== true || failedIssues.length > 0) {
				const { message, errorDetails } = extractErrorFromBulkMutationResponse(data);
				const errorToReport =
					failedIssues.length > 0
						? new Error('Some issues failed to trigger Agent')
						: new Error(message ?? 'Failed to trigger Autodev Rovo agent in bulk');
				onTriggerAgentInBulkError({
					failedIssueInfo: failedIssues.filter(Boolean).map((failedIssue) => ({
						issueKey: failedIssue?.issue?.key ?? '',
						conversationId: failedIssue?.conversationId ?? '',
						issueUrl: failedIssue?.issue?.webUrl ?? '',
					})),
					agentId,
					agentName,
					issueAris,
					projectKey,
					showFlag,
					formatMessage,
					duration,
					error: errorToReport,
					errorDetails: failedIssues.length > 0 ? undefined : errorDetails,
					isSimplifiedProject,
				});

				return;
			}

			// All succeeded
			fireOperationalAnalytics(createAnalyticsEvent({}), 'triggerAutodevRovoAgentInBulk success', {
				agentId,
				issueAris: issueAris.toString(),
				duration,
				totalIssues: issueAris.length,
				failedIssues: failedIssues.length,
				failedIssuesWithoutConversationId: failedIssues.filter(
					(failedIssue) => failedIssue?.conversationId == null,
				),
			});
			showFlag({
				messageId:
					'autodev-agent-picker.ui.picker-content.show-flag.success.picker-content-bulk-run.show-flag.all-succeeded',
				messageType: 'transactional',
				type: 'success',
				title: formatMessage(messages.agentPickerInBulkAllSuccessFlagTitle, {
					agentName,
					issueCount,
				}),
				description: fg('jira-development-work-items-agent-tab')
					? undefined
					: formatMessage(messages.agentPickerInBulkAllSuccessFlagDescriptionBeforeDevHub, {
							agentName,
							issueCount,
						}),
				actions: fg('jira-development-work-items-agent-tab')
					? [
							{
								content: formatMessage(messages.viewInDevelopment),
								href: `/jira/software/${isSimplifiedProject ? '' : 'c/'}projects/${projectKey}/development?tab=AGENTS`,
								target: '_blank',
								onClick: (_?: React.MouseEvent<HTMLElement>, analyticsEvent?: UIAnalyticsEvent) => {
									if (analyticsEvent != null) {
										fireUIAnalytics(analyticsEvent, 'viewInDevelopment', {
											flagType: 'afterAllSuccessful',
										});
									}
								},
							},
						]
					: undefined,
				testId: 'autodev-agent-picker.ui.picker-content.invoke-autodev-success-flag',
			});
		},
		onTriggerMutationInBulkStarted: (agentName: string, issueCount: number) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'agentPickerBulk');
			showFlag({
				messageId:
					'autodev-agent-picker.ui.picker-content.show-flag.info.picker-content-bulk-run.show-flag.started',
				messageType: 'transactional',
				type: 'info',
				title: formatMessage(messages.agentPickerInBulkStartInfoFlagTitle, {
					agentName,
					issueCount,
				}),
				description: formatMessage(messages.agentPickerInBulkStartInfoFlagDescription),
				testId: 'autodev-agent-picker.ui.picker-content.invoke-autodev-started-flag',
			});
		},
		onTriggerMutationInBulkOverLimit: () => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'agentPickerBulk', {
				isLimitReached: true,
			});
			showFlag({
				messageId:
					'autodev-agent-picker.ui.picker-content.show-flag.error.flag.error.show-flag.error.info.picker-content-bulk-run-over-limit-error-flag',
				messageType: 'transactional',
				type: 'error',
				title: formatMessage(messages.agentPickerInBulkAttemptedOverLimitFlagTitle),
				description: `${formatMessage(messages.agentPickerInBulkAttemptedOverLimitFlagDescription, {
					limit: BULK_LIMIT,
				})} ${formatMessage(messages.agentPickerInBulkAttemptedOverLimitFlagDescription2, {
					limit: BULK_LIMIT,
				})}`,
			});
		},
	};
};

export const emptyJobListStore = (
	store: RecordSourceSelectorProxy,
	jobListRelayStoreId: string | null,
) => {
	const jobListRecord = store.get(jobListRelayStoreId ?? '');
	if (!jobListRecord) {
		return;
	}
	jobListRecord.setLinkedRecords([], 'nodes');
};

export const onDeleteJobMutationUpdater = (
	store: RecordSourceSelectorProxy,
	isSuccessful: boolean,
	jobListRelayStoreId: string | null,
) => {
	if (isSuccessful) {
		emptyJobListStore(store, jobListRelayStoreId);
	}
};

export const useRovoChat = () => {
	const publish = usePublish('ai-mate');
	const openChat = useCallback(
		(channelId: string) => {
			publish({
				type: 'chat-open',
				product: 'jira',
				data: {
					channelId,
				},
				source: 'autodev',
			});
		},
		[publish],
	);
	return { openChat };
};

export const shouldShowJobLoadingState = ({
	screen,
	isTriggeringAutodevInprogress,
	jobPhase,
	jobStatus,
	isAgentJob,
}: {
	screen: Screen;
	isTriggeringAutodevInprogress: boolean;
	jobPhase: JiraAutodevPhase | null;
	jobStatus: JiraAutodevStatus | null;
	isAgentJob: boolean;
}): boolean => {
	if (isTriggeringAutodevInprogress) {
		// We need this check to because we can't know isAgentJob before job is returned from the network until mutation response comes back with job info
		return true;
	}

	if (isAgentJob) {
		return isTriggeringAutodevInprogress || isAgentTriggeredJobInProgress(jobPhase, jobStatus);
	}
	// If the user has clicked the trigger button in the normal Autodev flow,
	// we optimistically show the processing screen until the actual job is returned
	// from the network
	return PROCESSING_SCREENS.includes(screen);
};

// eslint-disable-next-line @atlassian/relay/must-use-inline -- This is to hit /slow endpoint that is used by commitMutation. We can clean this up when autodev_-_invoke_agent_slow_endpoint is cleaned-up
export const InvokeAgentMutation = graphql`
	mutation utils_triggerRovoAgentMutation($issueAri: ID!, $agentId: ID!) {
		devai_invokeAutodevRovoAgent(issueId: $issueAri, agentId: $agentId)
			@optIn(to: "DevAiVerticalAutodev") {
			conversationId
			success
			errors {
				message
				extensions {
					errorType
					statusCode
				}
			}
		}
	}
`;

export const triggerAutodevSlowEndpoint = ({
	agentId,
	issueAris,
	onTriggerMutationCompleted,
	onTriggerMutationError,
	setIsTriggeringAutodevInprogress,
}: {
	agentId: string;
	issueAris: string[];
	onTriggerMutationCompleted: onTrigggerAgentMutationCompletedFn;
	onTriggerMutationError: onTriggerAgentMutationErrorFn;
	setIsTriggeringAutodevInprogress?: (isTriggeringInProgress: boolean) => void;
}) => {
	const start = Date.now();

	commitMutation<utils_triggerRovoAgentMutation>(getRelayEnvironment(), {
		mutation: InvokeAgentMutation,
		cacheConfig: { metadata: { META_SLOW_ENDPOINT: true } },
		variables: { issueAri: issueAris[0], agentId },
		onCompleted: (mutationResponse: utils_triggerRovoAgentMutation$data) => {
			const duration = Date.now() - start;
			onTriggerMutationCompleted(mutationResponse, agentId, duration);
		},
		onError: (e: Error) => {
			const duration = Date.now() - start;
			setIsTriggeringAutodevInprogress?.(false);
			onTriggerMutationError(agentId, duration, e);
		},
	});
};

export const triggerAutodevBulkSlowEndpoint = ({
	agentId,
	agentName,
	issueCount,
	issueAris,
	onTriggerMutationInBulkCompleted,
	onTriggerMutationInBulkError,
}: {
	agentId: string;
	agentName: string;
	issueCount: number;
	issueAris: string[];
	onTriggerMutationInBulkCompleted: onTrigggerAgentInBulkMutationCompletedFn;
	onTriggerMutationInBulkError: onTriggerAgentInBulkMutationErrorFn;
}) => {
	const start = Date.now();

	commitMutation<utils_triggerRovoAgentInBulkMutation>(getRelayEnvironment(), {
		// eslint-disable-next-line @atlassian/relay/must-use-inline -- Special case for slow endpoint
		mutation: graphql`
			mutation utils_triggerRovoAgentInBulkMutation($issueAris: [ID!]!, $agentId: ID!) {
				devai_invokeAutodevRovoAgentInBulk(issueIds: $issueAris, agentId: $agentId)
					@optIn(to: "DevAiVerticalAutodev") {
					failedIssues {
						conversationId
						issueId
						issue {
							key
							webUrl
						}
					}
					succeededIssues {
						conversationId
						issueId
					}
					success
					errors {
						message
						extensions {
							errorType
							statusCode
						}
					}
				}
			}
		`,
		cacheConfig: { metadata: { META_SLOW_ENDPOINT: true } },
		variables: { issueAris, agentId },
		onCompleted: (mutationResponse: utils_triggerRovoAgentInBulkMutation$data) => {
			const duration = Date.now() - start;
			onTriggerMutationInBulkCompleted({
				data: mutationResponse,
				agentId,
				agentName,
				issueCount,
				duration,
			});
		},
		onError: (e: Error) => {
			const duration = Date.now() - start;
			onTriggerMutationInBulkError({ agentId, agentName, duration, e, failedIssueInfo: [] });
		},
	});
};

const getIsSimplifiedProject = () => {
	// TODO: Fix this properly. Our original `useIsSimplifiedProject` approach didn't work consistently because
	// the project context isn't always available outside of the issue view. We should fetch it via Relay instead.
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return window.location.pathname.includes('/c/') === false;
};
